import React, { useState } from "react";
import { ListChecks, ChevronRight } from "lucide-react";

export interface QuizCardProps {
  question: string;
  options: string[];
  correctIndex: number;
  /** Ответ пользователя из БД (при загрузке истории): null = не ответил, number = индекс */
  initialSelectedIndex?: number | null;
  onAnswer?: (correct: boolean, selectedIndex: number) => void;
}

export const QuizCard: React.FC<QuizCardProps> = ({
  question,
  options,
  correctIndex,
  initialSelectedIndex,
  onAnswer,
}) => {
  const [open, setOpen] = useState(true);
  const [selected, setSelected] = useState<number | null>(initialSelectedIndex ?? null);
  const [revealed, setRevealed] = useState(initialSelectedIndex != null);
  const safeCorrectIndex = Math.max(0, Math.min(correctIndex, options.length - 1));

  const handleClick = (idx: number) => {
    if (revealed) return;
    setSelected(idx);
    setRevealed(true);
    onAnswer?.(idx === safeCorrectIndex, idx);
  };

  return (
    <div className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50/60 shadow-sm overflow-hidden">
      {/* Header — always visible, toggles content */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-indigo-100/50 transition-colors"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 shrink-0">
          <ListChecks className="w-3.5 h-3.5" strokeWidth={2.25} />
        </span>
        <span className="text-sm font-semibold text-indigo-900 flex-1">Проверь себя</span>
        <ChevronRight
          className={`w-3.5 h-3.5 text-indigo-400 transition-transform duration-150 ${open ? "rotate-90" : ""}`}
        />
      </button>

      {/* Collapsible content */}
      {open && (
        <div className="px-3 pb-3 border-t border-indigo-100">
          <p className="text-[11px] font-medium uppercase tracking-wide text-indigo-500/80 mt-2 mb-1.5">
            Один верный ответ
          </p>
          <p className="text-sm text-neutral-text mb-2.5">{question}</p>
          <div className="flex flex-col gap-1.5">
            {options.map((opt, idx) => {
              let btnClass =
                "text-left text-sm px-3 py-2 rounded-lg border transition-colors ring-1 ring-transparent ";
              if (!revealed) {
                btnClass +=
                  "border-indigo-200/80 bg-white hover:border-indigo-400/60 hover:bg-indigo-50/50 cursor-pointer";
              } else if (idx === safeCorrectIndex) {
                btnClass += "border-green-500/50 bg-green-50 text-green-800 cursor-default ring-green-200/50";
              } else if (idx === selected && idx !== safeCorrectIndex) {
                btnClass += "border-red-300 bg-red-50 text-red-700 cursor-default";
              } else {
                btnClass += "border-neutral-border/60 bg-neutral-light/50 text-neutral-text/60 cursor-default";
              }
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleClick(idx)}
                  className={btnClass}
                  disabled={revealed}
                >
                  <span className="font-semibold mr-1.5 tabular-nums text-indigo-500/90">
                    {String.fromCharCode(65 + idx)}.
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>
          {revealed && selected === safeCorrectIndex && (
            <p className="mt-2 text-xs text-green-600 font-medium">Правильно!</p>
          )}
          {revealed && selected !== safeCorrectIndex && (
            <p className="mt-2 text-xs text-neutral-text/70">
              Правильный ответ: {options[safeCorrectIndex]}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
